import React, { useCallback, useEffect } from 'react';
import { Modal, Text, TextInput, Button, PhoneInput, SelectInput, Icon } from '@/ui-kits';
import { useFormik } from 'formik';
import { EditHospitalSpecialistRequestPayload, InvitedSpecialist } from '@/interface';
import { EditHospitalSpecialistFormValidation } from '@/utils/schema';
import { hospitalPocProfileSpecializationsSelector, hospitalPocSpecialistInviteLoadingSelector } from '@/services/redux/selectors';
import { useSelector } from 'react-redux';
import { createFormattedBlurHandler } from '@/utils/common_functions';
import { HospitalSpecialistOnboardingStatus } from '@/constants';

interface EditHospitalSpecialistModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (values: EditHospitalSpecialistRequestPayload) => void;
  specialist: InvitedSpecialist;
}

interface EditHospitalSpecialistFormValues {
  id: number;
  name: string;
  email: string;
  phone: string;
  specialization: number;
}

export const EditHospitalSpecialistModal: React.FC<EditHospitalSpecialistModalProps> = props => {
  const { open, onClose, onSave, specialist } = props;

  // hooks
  const { values, errors, touched, handleBlur, handleSubmit, handleChange, setFieldValue, setValues, isValid, dirty } = useFormik<EditHospitalSpecialistFormValues>({
    initialValues: {
      id: specialist.id,
      name: specialist.name,
      email: specialist.email,
      phone: specialist.phone,
      specialization: specialist.specialization.id,
    },
    validationSchema: EditHospitalSpecialistFormValidation,
    enableReinitialize: true,
    onSubmit: onSave,
  });

  // selectors
  const specializations = useSelector(hospitalPocProfileSpecializationsSelector);
  const isloading = useSelector(hospitalPocSpecialistInviteLoadingSelector);

  // Check if specialist has already registered (status is not Invited)
  const isSpecialistRegistered = specialist.onboarding_status.id !== HospitalSpecialistOnboardingStatus.Invited;

  // Create formatted blur handler for name field to capitalize first letters
  const handleNameBlur = createFormattedBlurHandler(handleBlur, setFieldValue, 'name');

  const closeModal = useCallback(() => {
    if (!dirty) {
      onClose();
    }
  }, [dirty, onClose]);

  // Update form values when specialist changes
  useEffect(() => {
    setValues({
      id: specialist.id,
      name: specialist.name,
      email: specialist.email,
      phone: specialist.phone,
      specialization: specialist.specialization.id,
    });
  }, [specialist, setValues]);

  return (
    <Modal
      open={open}
      onClose={closeModal}
      className="relative backdrop:bg-[#0000007e] bg-white py-4 sm:py-6 px-4 sm:px-6 md:px-8 rounded-xl gap-4 w-[90vw] max-w-[500px] shadow-lg">
      <Text variant="heading" className="text-ui_blue">
        Edit Specialist Details
      </Text>

      <div className="flex flex-col gap-2 sm:gap-4 my-5">
        {/* Admin Name */}
        <TextInput
          name="name"
          label="Full Name"
          value={values.name}
          onChangeText={handleChange('name')}
          onBlur={handleNameBlur as any}
          error={errors.name}
          touched={touched.name}
          required
          preventLeadingSpace
          preventTrailingSpace
          placeholder="Enter specialist name"
          wrapperClassName="border w-full"
          disabled={isSpecialistRegistered}
        />

        {/* Email Address */}
        <TextInput
          name="email"
          label="Email Address"
          value={values.email}
          onChangeText={handleChange('email')}
          onBlur={handleBlur('email')}
          error={errors.email}
          touched={touched.email}
          required
          placeholder="Enter specialist email ID"
          wrapperClassName="border w-full"
          disabled={isSpecialistRegistered}
        />

        {/* Phone Number */}
        <PhoneInput
          name="phone"
          label="Phone"
          value={values.phone}
          onChange={phone => setFieldValue('phone', phone)}
          onBlur={handleBlur('phone')}
          error={errors.phone}
          touched={touched.phone}
          required
          placeholder="Enter specialist phone no."
          wrapperClassName="border w-full"
          disabled={isSpecialistRegistered}
        />

        <SelectInput
          name="specialization"
          label="Specialization"
          value={values.specialization}
          onChange={item => setFieldValue('specialization', item.id)}
          onBlur={handleBlur('specialization')}
          error={errors.specialization}
          touched={touched.specialization}
          required
          placeholder="Select specialization"
          wrapperClassName="border w-full"
          options={specializations}
          dropdownPosition="above"
          disabled={isSpecialistRegistered}
        />

      {/* Warning message when specialist has already registered */}
      {isSpecialistRegistered && (
        <div className="flex items-center gap-1">
          <Icon type="warning_setting" className="size-4 text-red-600 flex-shrink-0" />
          <Text className="text-red-600 text-sm whitespace-nowrap">
            Specialist has already registered. Editing is no longer allowed.
          </Text>
        </div>
      )}

      {/* Action Buttons */}
      </div>
      <div className="flex gap-4 items-center justify-center">
        <Button className="w-full max-w-[150px] py-2" variant="secondary" title="Cancel" onClick={onClose} />
        <Button className="w-full max-w-[160px] py-2" variant="primary" isLoading={isloading} title="Save Changes" onClick={() => handleSubmit()} disabled={!isValid || !dirty || isSpecialistRegistered} />
      </div>
    </Modal>
  );
};